/**
 * ReferentialItemList — rendu cmdk de la liste d'items pour les pickers de
 * référentiel (Profession, NirRefusalReason, valeurs de listes Qualité).
 *
 * Affiche les items avec Check sur la valeur sélectionnée et boutons
 * Pencil / Trash inline (visibles au survol) gated par `canManage`.
 *
 * ⚠️ HÉRITAGE — une valeur portée par une entité ANCÊTRE est proposée à la saisie
 * mais ne se modifie pas d'ici : les écritures sont scopées à l'entité propre, et
 * le serveur répond « introuvable » pour une valeur affichée un pixel au-dessus.
 * Les items marqués `inherited` portent donc la pastille « ↑ Hérité » et n'ont NI
 * crayon NI corbeille. Le geste de sortie n'est pas le renommage : c'est de
 * RECRÉER la valeur sous le même libellé dans son entité (la création est
 * réactivante et le dédoublonnage par code fait gagner l'entité propre).
 * Le champ est optionnel : les pickers mono-entité ne le renseignent pas et
 * gardent exactement le comportement d'avant.
 */
import { Check, Pencil, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import {
    CommandGroup,
    CommandItem,
} from '@/components/ui/command'
import { InheritedBadge } from '@/components/shared/InheritedBadge'

interface ReferentialItem {
    id: string
    name: string
    /** Valeur portée par une entité ancêtre : lisible, choisissable, non modifiable. */
    inherited?: boolean
    /** Nom de l'entité porteuse, pour l'infobulle de la pastille. */
    inheritedFrom?: string | null
}

interface ReferentialItemListProps<T extends ReferentialItem> {
    items: T[]
    value: string | null
    onSelect: (id: string) => void
    canManage: boolean
    onEdit: (item: T) => void
    onDelete: (item: T) => void
    headingKey?: string
    headingFallback?: string
}

export function ReferentialItemList<T extends ReferentialItem>({
    items,
    value,
    onSelect,
    canManage,
    onEdit,
    onDelete,
    headingKey,
    headingFallback,
}: ReferentialItemListProps<T>) {
    const { t } = useTranslation()
    if (items.length === 0) return null
    return (
        <CommandGroup
            heading={
                headingKey
                    ? t(headingKey, headingFallback ?? '')
                    : undefined
            }
        >
            {items.map((it) => (
                <CommandItem
                    key={it.id}
                    value={it.id}
                    onSelect={() => onSelect(it.id)}
                    className="group"
                >
                    <span className="flex-1 truncate">{it.name}</span>
                    {it.inherited && <InheritedBadge fromName={it.inheritedFrom} />}
                    {it.id === value && (
                        <Check className="ml-2 h-4 w-4" />
                    )}
                    {canManage && !it.inherited && (
                        <span
                            className="ml-2 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 group-data-[selected=true]:opacity-100"
                            // cmdk sélectionne l'item sur pointerdown : sans stopPropagation
                            // ici, le clic sur le crayon/corbeille est intercepté par le
                            // CommandItem (ferme le popover) AVANT que le bouton reçoive son
                            // onClick → l'édition/suppression ne s'ouvre jamais.
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 [&_svg]:pointer-events-auto"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onEdit(it)
                                }}
                                aria-label={t('common.rename', 'Renommer')}
                            >
                                <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                                type="button"
                                variant="destructive-ghost"
                                size="icon"
                                className="h-6 w-6 [&_svg]:pointer-events-auto"
                                onClick={(e) => {
                                    e.stopPropagation()
                                    onDelete(it)
                                }}
                                aria-label={t('common.delete', 'Supprimer')}
                            >
                                <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                        </span>
                    )}
                </CommandItem>
            ))}
        </CommandGroup>
    )
}
